import { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ContentModal } from '@localstack/ui';
import { PERMISSION_GROUPS, PERMISSION_TYPES, ROLE_MAPPINGS } from '@localstack/constants';
import { Permission, UserMembershipView } from '@localstack/types';

import {
  Business as OrganizationIcon,
  CreditCard as PaymentIcon,
  ShoppingCart as SubscriptionsIcon,
  Key as CIAuthTokensIcon,
  LocalPolice as LicensesIcon,
} from '@mui/icons-material';

import { CompactCheckbox, CompactFormControlLabel } from './style';

export interface EditMemberPermissionsModalProps {
  member: UserMembershipView;
  onClose: () => void;
  onTogglePermission: (userId: string, permission: Permission) => Promise<void>;
}

const categoryIconMap: Record<keyof typeof PERMISSION_GROUPS, JSX.Element> = {
  Organization: <OrganizationIcon sx={{ height: (theme) => theme.spacing(2) }} />,
  'Payment Details': <PaymentIcon sx={{ height: (theme) => theme.spacing(2) }} />,
  Subscriptions: <SubscriptionsIcon sx={{ height: (theme) => theme.spacing(2) }} />,
  'CI Auth Tokens': <CIAuthTokensIcon sx={{ height: (theme) => theme.spacing(2) }} />,
  Licenses: <LicensesIcon sx={{ height: (theme) => theme.spacing(2) }} />,
};

export const EditMemberPermissionsModal = ({
  member,
  onClose,
  onTogglePermission,
}: EditMemberPermissionsModalProps): JSX.Element => {
  const [isBusy, setIsBusy] = useState(false);

  const defaultPermissions = useMemo(
    () => ROLE_MAPPINGS[member.membership_details.role],
    [member.membership_details.role],
  );
  const memberPermissions = member.membership_details.permissions;

  const togglePermission = useCallback(
    async (permission: Permission) => {
      setIsBusy(true);
      await onTogglePermission(member.user_id, permission);
      setIsBusy(false);
    },
    [member.user_id, onTogglePermission],
  );

  return (
    <ContentModal
      modalMaxWidth="sm"
      open={true}
      onClose={onClose}
      fullWidth
      backdropBlurIntensity={3}
      dialogTitle={`Edit permissions for ${member.user_email}`}
      dialogContent={
        <Box>
          {Object.entries(PERMISSION_GROUPS).map(([title, perms]) => (
            <Box mb={1} key={title}>
              <Box p={1} mb={1} sx={{ backgroundColor: (theme) => theme.palette.background.default }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {categoryIconMap[title as keyof typeof PERMISSION_GROUPS]}
                  <Typography color="text.primary" fontWeight={600}>
                    {title}
                  </Typography>
                </Stack>
              </Box>
              <Stack direction="column">
                {perms.map((permission) => (
                  <CompactFormControlLabel
                    key={permission}
                    label={PERMISSION_TYPES[permission as keyof typeof PERMISSION_TYPES]}
                    disabled={defaultPermissions.includes(permission) || isBusy}
                    checked={defaultPermissions.includes(permission) || memberPermissions.includes(permission)}
                    onChange={() => togglePermission(permission)}
                    control={<CompactCheckbox size="small" />}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      }
    />
  );
};
